import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useEloRankings } from "../../hooks/useEloRankings";
import { PARAM_RANGES, CONFERENCE_COLORS, POWER_CONFERENCES } from "../../utils/eloConstants";
import SEO from "../../components/common/SEO";

// Import data
import gamesData from "../../data/eloGames2025.json";
import teamsData from "../../data/eloTeams.json";
import officialEloData from "../../data/officialElo2025.json";

// Info icon with tooltip popover
function InfoTooltip({ param, range }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-600 text-[10px] text-slate-300 transition-colors hover:bg-amber-500 hover:text-slate-900"
        aria-label={`Info about ${range.label}`}
      >
        ?
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-6 z-50 w-72 rounded-lg bg-slate-900 p-4 shadow-xl ring-1 ring-slate-700"
            style={{ minWidth: "280px" }}
          >
            {/* Header */}
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-slate-900">
                {param === "baseK" && "K"}
                {param === "marginWeight" && "M"}
                {param === "maxMarginMultiplier" && "C"}
                {param === "homeAdvantage" && "H"}
                {param === "conferenceBias" && "B"}
              </div>
              <h3 className="font-semibold text-white">{range.label}</h3>
            </div>

            {/* Description */}
            <p className="mb-3 text-sm leading-relaxed text-slate-300">{range.description}</p>

            {/* Visual scale */}
            <div className="mb-3 rounded-lg bg-slate-800 p-3">
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
                Effect Scale
              </div>
              <div className="space-y-2">
                {Object.entries(range.visual).map(([level, data]) => (
                  <div key={level} className="flex items-center gap-2">
                    <div
                      className={`h-2 w-2 rounded-full ${
                        level === "low"
                          ? "bg-blue-400"
                          : level === "mid"
                            ? "bg-amber-400"
                            : "bg-red-400"
                      }`}
                    />
                    <span className="min-w-[80px] text-xs font-medium text-slate-400">
                      {data.label}
                    </span>
                    <span className="text-xs text-slate-500">{data.effect}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Example */}
            <div className="rounded-lg bg-slate-800/50 p-2">
              <div className="flex items-start gap-2">
                <span className="text-amber-400">💡</span>
                <p className="text-xs leading-relaxed text-slate-400">{range.example}</p>
              </div>
            </div>

            {/* Arrow pointer */}
            <div className="absolute -top-1.5 left-3 h-3 w-3 rotate-45 bg-slate-900 ring-1 ring-slate-700" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CFBEloRankings() {
  const {
    rankings,
    comparison,
    params,
    updateParam,
    resetParams,
    currentWeek,
    setCurrentWeek,
    availableWeeks,
    totalWeeks,
  } = useEloRankings(teamsData, gamesData, officialEloData);

  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [conferenceFilter, setConferenceFilter] = useState(null);

  // Filter rankings by conference if selected
  const filteredRankings = useMemo(() => {
    if (!conferenceFilter) return rankings;
    return rankings.filter((t) => t.conference === conferenceFilter);
  }, [rankings, conferenceFilter]);

  // Top 12 for playoff display
  const playoffTeams = rankings.slice(0, 12);

  // Get unique conferences for filter
  const conferences = useMemo(() => {
    const confs = [...new Set(rankings.map((t) => t.conference))];
    return confs.sort((a, b) => {
      // Power conferences first
      const aIsPower = POWER_CONFERENCES.includes(a);
      const bIsPower = POWER_CONFERENCES.includes(b);
      if (aIsPower && !bIsPower) return -1;
      if (!aIsPower && bIsPower) return 1;
      return a.localeCompare(b);
    });
  }, [rankings]);

  return (
    <>
      <SEO
        title="CFB ELO Rankings | Polity Interfaces"
        description="Interactive Chess-style ELO rating system for College Football. Adjust parameters and compare with the CFP Selection Committee."
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Header */}
        <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-40">
          <div className="mx-auto max-w-7xl px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">
                  CFB ELO Rankings
                  <span className="ml-2 text-sm font-normal text-slate-400">2025 Season</span>
                </h1>
                <p className="text-sm text-slate-400">
                  Chess-style rating system with configurable parameters
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowComparison(!showComparison)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    showComparison
                      ? "bg-amber-500 text-slate-900"
                      : "bg-slate-700 text-slate-200 hover:bg-slate-600"
                  }`}
                >
                  {showComparison ? "Hide Comparison" : "Compare to Official"}
                </button>
                <button
                  onClick={resetParams}
                  className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-600"
                >
                  Reset Parameters
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl px-4 py-6">
          {/* Parameter Controls */}
          <div className="mb-6 rounded-xl bg-slate-800/50 p-4 backdrop-blur-sm">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-400">
              Algorithm Parameters
            </h2>
            <div className="grid gap-6 md:grid-cols-5">
              {Object.entries(PARAM_RANGES).map(([key, range]) => (
                <div key={key}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <label className="text-sm text-slate-300">{range.label}</label>
                      <InfoTooltip param={key} range={range} />
                    </div>
                    <span className="text-sm font-mono text-amber-400">
                      {params[key].toFixed(key === "marginWeight" ? 2 : 0)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={range.min}
                    max={range.max}
                    step={range.step}
                    value={params[key]}
                    onChange={(e) => updateParam(key, parseFloat(e.target.value))}
                    className="h-2 w-full cursor-pointer appearance-none rounded-lg bg-slate-700 accent-amber-500"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Week Timeline */}
          <div className="mb-6 rounded-xl bg-slate-800/50 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
                Season Timeline
              </h2>
              <span className="text-sm text-slate-300">
                {currentWeek === null ? "Final Rankings" : `Week ${currentWeek}`}
              </span>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => setCurrentWeek(0)}
                className={`rounded px-2 py-1 text-xs ${
                  currentWeek === 0
                    ? "bg-amber-500 text-slate-900"
                    : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                }`}
              >
                Pre
              </button>
              {availableWeeks
                .filter((w) => w > 0)
                .map((week) => (
                  <button
                    key={week}
                    onClick={() => setCurrentWeek(week)}
                    className={`rounded px-2 py-1 text-xs ${
                      currentWeek === week
                        ? "bg-amber-500 text-slate-900"
                        : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                    }`}
                  >
                    {week}
                  </button>
                ))}
              <button
                onClick={() => setCurrentWeek(null)}
                className={`rounded px-3 py-1 text-xs font-medium ${
                  currentWeek === null
                    ? "bg-amber-500 text-slate-900"
                    : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                }`}
              >
                Final
              </button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Playoff Bracket - Top 12 */}
            <div className="lg:col-span-1">
              <div className="rounded-xl bg-slate-800/50 p-4 backdrop-blur-sm">
                <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-400">
                  12-Team Playoff
                </h2>
                <div className="space-y-2">
                  {playoffTeams.map((team, index) => (
                    <motion.div
                      key={team.team}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => setSelectedTeam(team)}
                      className={`group flex cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors ${
                        selectedTeam?.team === team.team
                          ? "bg-slate-700"
                          : "hover:bg-slate-700/50"
                      } ${index < 4 ? "border-l-4 border-amber-500" : ""}`}
                    >
                      <div
                        className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white"
                        style={{
                          backgroundColor: team.color
                            ? `#${team.color.replace("#", "")}`
                            : "#666",
                        }}
                      >
                        {team.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm font-medium text-white">
                          {team.team}
                        </div>
                        <div className="text-xs text-slate-400">
                          {team.wins}-{team.losses} · {team.conference}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono text-amber-400">
                          {Math.round(team.rating)}
                        </div>
                        <div className="text-xs text-slate-500">±{Math.round(team.rd)}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-4 border-t border-slate-700 pt-4">
                  <p className="text-xs text-slate-500">
                    Top 4 teams (gold border) receive first-round byes
                  </p>
                </div>
              </div>

              {/* Comparison Stats */}
              {showComparison && comparison && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 rounded-xl bg-slate-800/50 p-4 backdrop-blur-sm"
                >
                  <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-400">
                    vs Official ELO
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-slate-400">Rank Correlation</div>
                      <div className="text-2xl font-bold text-amber-400">
                        {(comparison.spearmanCorrelation * 100).toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-slate-400">Avg Rank Difference</div>
                      <div className="text-2xl font-bold text-white">
                        {comparison.averageRankDiff.toFixed(1)} spots
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Full Rankings Table */}
            <div className="lg:col-span-2">
              <div className="rounded-xl bg-slate-800/50 p-4 backdrop-blur-sm">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">
                    Full Rankings
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setConferenceFilter(null)}
                      className={`rounded px-2 py-1 text-xs ${
                        !conferenceFilter
                          ? "bg-amber-500 text-slate-900"
                          : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                      }`}
                    >
                      All
                    </button>
                    {conferences.slice(0, 8).map((conf) => (
                      <button
                        key={conf}
                        onClick={() => setConferenceFilter(conf)}
                        className={`rounded px-2 py-1 text-xs ${
                          conferenceFilter === conf
                            ? "text-slate-900"
                            : "text-slate-300 hover:bg-slate-600"
                        }`}
                        style={{
                          backgroundColor:
                            conferenceFilter === conf
                              ? CONFERENCE_COLORS[conf] || "#666"
                              : undefined,
                        }}
                      >
                        {conf}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-slate-800">
                      <tr className="text-left text-xs uppercase text-slate-400">
                        <th className="p-2">Rank</th>
                        <th className="p-2">Team</th>
                        <th className="p-2">Record</th>
                        <th className="p-2">Conference</th>
                        <th className="p-2 text-right">Rating</th>
                        <th className="p-2 text-right">±RD</th>
                        {showComparison && <th className="p-2 text-right">Δ</th>}
                      </tr>
                    </thead>
                    <tbody>
                      <AnimatePresence>
                        {filteredRankings.map((team) => {
                          const compData = comparison?.comparisons.find(
                            (c) => c.team === team.team
                          );
                          return (
                            <motion.tr
                              key={team.team}
                              layout
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              onClick={() => setSelectedTeam(team)}
                              className={`cursor-pointer border-b border-slate-700/50 transition-colors ${
                                selectedTeam?.team === team.team
                                  ? "bg-slate-700"
                                  : "hover:bg-slate-700/30"
                              }`}
                            >
                              <td className="p-2">
                                <span
                                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                                    team.rank <= 4
                                      ? "bg-amber-500 text-slate-900"
                                      : team.rank <= 12
                                        ? "bg-green-600 text-white"
                                        : "bg-slate-600 text-slate-300"
                                  }`}
                                >
                                  {team.rank}
                                </span>
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="h-3 w-3 rounded-full"
                                    style={{
                                      backgroundColor: team.color
                                        ? `#${team.color.replace("#", "")}`
                                        : "#666",
                                    }}
                                  />
                                  <span className="text-sm text-white">{team.team}</span>
                                </div>
                              </td>
                              <td className="p-2 text-sm text-slate-300">
                                {team.wins}-{team.losses}
                              </td>
                              <td className="p-2">
                                <span
                                  className="rounded px-2 py-0.5 text-xs"
                                  style={{
                                    backgroundColor: `${CONFERENCE_COLORS[team.conference] || "#666"}33`,
                                    color: CONFERENCE_COLORS[team.conference] || "#999",
                                  }}
                                >
                                  {team.conference}
                                </span>
                              </td>
                              <td className="p-2 text-right font-mono text-sm text-amber-400">
                                {Math.round(team.rating)}
                              </td>
                              <td className="p-2 text-right font-mono text-xs text-slate-500">
                                {Math.round(team.rd)}
                              </td>
                              {showComparison && (
                                <td className="p-2 text-right">
                                  {compData && (
                                    <span
                                      className={`text-xs font-medium ${
                                        compData.rankDiff > 0
                                          ? "text-red-400"
                                          : compData.rankDiff < 0
                                            ? "text-green-400"
                                            : "text-slate-400"
                                      }`}
                                    >
                                      {compData.rankDiff > 0 ? "+" : ""}
                                      {compData.rankDiff}
                                    </span>
                                  )}
                                </td>
                              )}
                            </motion.tr>
                          );
                        })}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Team Detail */}
          <AnimatePresence>
            {selectedTeam && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="fixed bottom-4 left-4 right-4 mx-auto max-w-4xl rounded-xl bg-slate-800 p-6 shadow-2xl md:bottom-8"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className="flex h-16 w-16 items-center justify-center rounded-xl text-2xl font-bold text-white"
                      style={{
                        backgroundColor: selectedTeam.color
                          ? `#${selectedTeam.color.replace("#", "")}`
                          : "#666",
                      }}
                    >
                      #{selectedTeam.rank}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{selectedTeam.team}</h3>
                      <p className="text-slate-400">
                        {selectedTeam.wins}-{selectedTeam.losses} · {selectedTeam.conference}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTeam(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Rating History */}
                <div className="mt-6">
                  <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">
                    Rating History
                  </h4>
                  <div className="flex h-24 items-end gap-1">
                    {selectedTeam.history?.map((h, i) => {
                      const minRating = Math.min(...selectedTeam.history.map((x) => x.rating));
                      const maxRating = Math.max(...selectedTeam.history.map((x) => x.rating));
                      const range = maxRating - minRating || 1;
                      const height = ((h.rating - minRating) / range) * 100;

                      return (
                        <div
                          key={i}
                          className="group relative flex-1"
                          title={`Week ${h.week}: ${Math.round(h.rating)}`}
                        >
                          <div
                            className="w-full rounded-t bg-amber-500 transition-all group-hover:bg-amber-400"
                            style={{ height: `${Math.max(height, 5)}%` }}
                          />
                          {h.opponent && (
                            <div className="absolute -top-16 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs group-hover:block">
                              <span className={h.result === "W" ? "text-green-400" : "text-red-400"}>
                                {h.result}
                              </span>{" "}
                              vs {h.opponent}
                              <br />
                              {h.score} ({h.change > 0 ? "+" : ""}{h.change.toFixed(0)})
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex justify-between text-xs text-slate-500">
                    <span>Pre</span>
                    <span>Week {selectedTeam.history?.length - 1}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="mt-6 grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-slate-400">Current Rating</div>
                    <div className="text-2xl font-bold text-amber-400">
                      {Math.round(selectedTeam.rating)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-400">Uncertainty</div>
                    <div className="text-2xl font-bold text-white">±{Math.round(selectedTeam.rd)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-400">Initial Rating</div>
                    <div className="text-2xl font-bold text-slate-300">
                      {Math.round(selectedTeam.initialRating)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-400">Change</div>
                    <div
                      className={`text-2xl font-bold ${
                        selectedTeam.rating > selectedTeam.initialRating
                          ? "text-green-400"
                          : "text-red-400"
                      }`}
                    >
                      {selectedTeam.rating > selectedTeam.initialRating ? "+" : ""}
                      {Math.round(selectedTeam.rating - selectedTeam.initialRating)}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
